/* eslint-disable max-lines */
import moment from 'moment';
import { useCallback, useState } from 'react';
import { useAsyncFn, useMount, useUpdateEffect } from 'react-use';

import { PractitionerId } from '@dreryk/egabinet-personnel-published-language-api';
import { AppointmentPractitioner, AppointmentSlotScheduleFreeSlot } from '@dreryk/egabinet-appointment-frontend-api';

import { dispatchSnackbarNegativeEvent } from '@dreryk/react-components';

import { AppointmentHoverSlotData, AppointmentSchedule } from '~libs.shared.appointments-timetable';
import { createLocalStorageState } from '~libs.ui.react';
import { createLocalStorage } from '~libs.typescript';

import { calendarTranslationKeys } from '../../../translations';
import { CalendarSelectedDate } from '../../CalendarController.types';

import { findNextAppointmentSlotScheduleFreeSlot, getNextAvailableTimeSlot } from './SpecialistTimetable.utils';
import { getWeekendsVisibilityPreferenceStorageKey } from './actions/form/SpecialistTimetablePreferencesForm.utils';

const ISO_WEEKDAY_SATURDAY = 6;
const ISO_WEEKDAY_SUNDAY = 7;

const isDateInWeekend = (date: Date | undefined): boolean =>
   [ISO_WEEKDAY_SATURDAY, ISO_WEEKDAY_SUNDAY].includes(moment(date).isoWeekday());
const getStartOfWeek = (date: Date): Date => moment.utc(date).startOf('week').startOf('day').toDate();
const getStartOfNextWeek = (date: Date): Date => moment(date).add(1, 'week').startOf('isoWeek').toDate();

const isDateInPastWeek = (date: Date): boolean => {
   const dateMoment = moment.utc(date);
   const dateWeekNumber = dateMoment.isoWeek();
   const dateYear = dateMoment.year();
   const currentDateMoment = moment.utc();
   const currentDateWeekNumber = currentDateMoment.isoWeek();
   const currentDateYear = currentDateMoment.year();
   return dateYear < currentDateYear || dateWeekNumber < currentDateWeekNumber;
};

const getNearestWeekendDate = (fromDate: Date): Date => {
   const beginDate = moment(fromDate).startOf('day');

   const saturday = beginDate.clone().isoWeekday(ISO_WEEKDAY_SATURDAY).startOf('day');
   if (saturday.isBefore(beginDate)) {
      return saturday.add(1, 'week').toDate();
   }

   return saturday.toDate();
};

const getNearestSearchDateForFirstAvailableTimeSlot = (
   timetableDate: Date,
   searchDate: Date | undefined,
   preferWeekendsVisible: boolean | undefined
): Date => {
   if (!searchDate && isDateInPastWeek(timetableDate)) {
      return getStartOfWeek(new Date());
   }

   if (!preferWeekendsVisible) {
      return getNearestWeekendDate(timetableDate);
   }

   return getStartOfNextWeek(timetableDate);
};

export const useWeekendsVisibilityPreference = createLocalStorageState(
   createLocalStorage<boolean>(getWeekendsVisibilityPreferenceStorageKey())
);

const findPractitionerById = (
   practitioners: AppointmentPractitioner[],
   practitionerId: number
): AppointmentPractitioner | undefined =>
   practitioners.find((practitioner) => practitionerId === practitioner.id.asNumber());

export const usePractitionerInitialSelection = (
   practitionerId: number | undefined,
   practitioners: AppointmentPractitioner[],
   setSelectedPractitioner: (practitioner: AppointmentPractitioner) => void
): void => {
   useMount(() => {
      if (practitionerId !== undefined) {
         const practitioner = findPractitionerById(practitioners, practitionerId);
         if (practitioner) {
            setSelectedPractitioner(practitioner);
         }
      }
   });

   useUpdateEffect(() => {
      if (practitionerId !== undefined) {
         const practitioner = findPractitionerById(practitioners, practitionerId);
         if (practitioner) {
            setSelectedPractitioner(practitioner);
         }
      }
   }, [practitionerId]);
};

const isSlotInOtherWeek = (
   slot: AppointmentSlotScheduleFreeSlot | undefined,
   currentDate: CalendarSelectedDate
): boolean => {
   if (!slot) {
      return false;
   }

   return !moment.parseZone(slot.timeSlot.getBeginDate()).isSame(moment.parseZone(currentDate), 'isoWeek');
};

type useFirstAvailableSlotToolsReturn = {
   nextAvailableTimeSlot: AppointmentHoverSlotData | undefined;
   onNextAvailableTimeSlotClick: VoidFunction;
   noAvailableSlots: boolean;
   nextAvailableTimeSlotSearchLoading: boolean;
   forceShowWeekends: boolean;
};
// eslint-disable-next-line max-lines-per-function
export const useNextAvailableTimeSlotTools = (
   appointmentSchedules: AppointmentSchedule[] | undefined | null,
   currentDate: CalendarSelectedDate,
   onDateWithFirstAvailableTimeSlotChange: (date: Date) => void,
   selectedPractitionerId: PractitionerId
   // eslint-disable-next-line max-params
): useFirstAvailableSlotToolsReturn => {
   const [preferWeekendsVisible] = useWeekendsVisibilityPreference();
   const [noAvailableSlots, setNoAvailableSlots] = useState<boolean>(false);
   const [nextAvailableTimeSlot, setNextAvailableTimeSlot] = useState<AppointmentHoverSlotData | undefined>();
   const [searchDate, setSearchDate] = useState<Date | undefined>();
   const [dateWithFirstAvailableTimeSlot, setDateWithFirstAvailableTimeSlot] = useState<Date | undefined>();

   const handleNoAvailableSlots = (): void => {
      setNoAvailableSlots(true);
      dispatchSnackbarNegativeEvent(calendarTranslationKeys.noAvailableTimeSlots);
   };
   const [searchDateChangeState, handleSearchDateChange] = useAsyncFn(async (): Promise<void> => {
      const nextAppointmentSlotScheduleFreeSlot = await findNextAppointmentSlotScheduleFreeSlot(
         selectedPractitionerId,
         searchDate!
      );
      if (nextAppointmentSlotScheduleFreeSlot) {
         const nextAvailableSlot = getNextAvailableTimeSlot(
            [nextAppointmentSlotScheduleFreeSlot],
            nextAvailableTimeSlot
         );
         if (!nextAvailableSlot) {
            if (moment.utc(getStartOfWeek(currentDate)).isSame(moment.utc(getStartOfWeek(searchDate!)))) {
               setSearchDate(getStartOfNextWeek(currentDate));
            } else {
               handleNoAvailableSlots();
            }
         }
         setNextAvailableTimeSlot(nextAvailableSlot);
         setDateWithFirstAvailableTimeSlot(nextAvailableSlot ? nextAvailableSlot.timeSlot.getBeginDate() : undefined);
      } else {
         handleNoAvailableSlots();
      }
   }, [searchDate, currentDate, selectedPractitionerId]);

   useUpdateEffect(() => {
      if (searchDate) {
         handleSearchDateChange();
      }
   }, [searchDate]);

   const onNextAvailableTimeSlotClick = useCallback(() => {
      if (!searchDate && isDateInPastWeek(currentDate)) {
         setSearchDate(getStartOfWeek(new Date()));
         return;
      }

      const currentSlotInOtherWeek = isSlotInOtherWeek(nextAvailableTimeSlot, currentDate);

      const [firstAppointmentSchedule] = appointmentSchedules ?? [];
      const nextTimeSlot = getNextAvailableTimeSlot(
         firstAppointmentSchedule.hoverSlots,
         currentSlotInOtherWeek ? undefined : nextAvailableTimeSlot
      );

      if (nextTimeSlot) {
         setNextAvailableTimeSlot(nextTimeSlot);
         return;
      }

      setSearchDate(getNearestSearchDateForFirstAvailableTimeSlot(currentDate, searchDate, preferWeekendsVisible));
   }, [searchDate, currentDate, appointmentSchedules, nextAvailableTimeSlot, preferWeekendsVisible]);

   const resetNextAvailableTimeSlot = useCallback(() => {
      setNextAvailableTimeSlot(undefined);
      setDateWithFirstAvailableTimeSlot(undefined);
      setSearchDate(undefined);
      setNoAvailableSlots(false);
   }, []);

   const forceShowWeekends = isDateInWeekend(nextAvailableTimeSlot?.timeSlot.getBeginDate());

   useUpdateEffect(resetNextAvailableTimeSlot, [selectedPractitionerId]);

   useUpdateEffect(() => {
      if (dateWithFirstAvailableTimeSlot) {
         onDateWithFirstAvailableTimeSlotChange(dateWithFirstAvailableTimeSlot);
      }
   }, [dateWithFirstAvailableTimeSlot]);

   return {
      nextAvailableTimeSlot: nextAvailableTimeSlot,
      onNextAvailableTimeSlotClick: onNextAvailableTimeSlotClick,
      noAvailableSlots: noAvailableSlots,
      nextAvailableTimeSlotSearchLoading: searchDateChangeState.loading,
      forceShowWeekends: forceShowWeekends,
   };
};
